import {
  FeatureFlag,
  FeatureFlags,
  FeatureFlagValue,
  ConfigFlags,
  MonetateFeatureFlag,
} from "../types.ts";
import { Experiences } from "../experiencesSchema.ts";
import { SearchPilotResult } from "../searchPilot/searchPilotSchema.ts";

const MULTI_EXPERIENCE_FLAG_NAMES = new Set([
  "disruptorBlockId",
  "disruptorBlockGridIndex",
  "disruptorBlockMaxWidth",
  "disruptorBlockEsiPath",
]);

export function buildFeatureFlags({
  defaultFlagValues,
  configFlags,
  parameterFlags,
  experiences,
  parameterPrefix,
  cookies,
  searchPilot,
}: {
  defaultFlagValues: Record<string, FeatureFlagValue>;
  configFlags?: ConfigFlags;
  parameterFlags?: Record<string, unknown>;
  experiences?: Experiences;
  parameterPrefix?: string;
  cookies?: Partial<Record<string, string>>;
  searchPilot?: SearchPilotResult;
}) {
  const defaultFlags = buildDefaultFlags(defaultFlagValues);
  return applyFeatureFlagOverrides({
    baseFlags: defaultFlags,
    experiences,
    parameterPrefix,
    cookies,
    parameterFlags,
    configFlags,
    searchPilot,
  });
}

function buildDefaultFlags(
  defaultFlagValues: Record<string, FeatureFlagValue>,
): FeatureFlags {
  const defaultFlags = {} as FeatureFlags;

  for (const [key, value] of Object.entries(defaultFlagValues)) {
    defaultFlags[key] = {
      source: "default",
      value,
    };
  }

  return defaultFlags;
}
export function applyFeatureFlagOverrides({
  baseFlags,
  experiences,
  parameterPrefix,
  cookies,
  parameterFlags,
  configFlags,
  searchPilot,
}: {
  baseFlags: FeatureFlags;
  experiences?: Experiences;
  parameterPrefix?: string;
  cookies?: Partial<Record<string, string>>;
  parameterFlags?: Record<string, unknown>;
  configFlags?: ConfigFlags;
  searchPilot?: SearchPilotResult;
}): FeatureFlags {
  const experienceOverrides = getExperienceOverrides(
    experiences,
    parameterPrefix,
  );

  const cookieOverrides = getCookieOverrides(
    cookies?.["FEATURE_FLAG_OVERRIDES"],
  );

  const searchPilotOverrides = getSearchPilotOverrides(searchPilot);

  const parameterOverrides = parameterFlags || {};

  const configOverrides = configFlags || {};

  const flags = {} as FeatureFlags;

  for (const [key, baseFlag] of Object.entries(baseFlags)) {
    switch (true) {
      // Order of precedence for feature flag overrides
      // 1. Cookie
      // 2. Monetate Experience
      // 3. Parameter (Product List HTML Attribute)
      // 4. SearchPilot
      // 5. Config File
      case key in cookieOverrides:
        flags[key] = cookieOverrides[key]!;
        break;
      case baseFlag.source === "cookie":
        flags[key] = baseFlag;
        break;
      case key in experienceOverrides:
        flags[key] = experienceOverrides[key]!;
        break;
      case baseFlag.source === "monetate":
        flags[key] = baseFlag;
        break;
      case key in parameterOverrides:
        flags[key] = {
          source: "parameter",
          value: getParameterValue(parameterOverrides[key]),
        };
        break;
      case baseFlag.source === "parameter":
        flags[key] = baseFlag;
        break;
      case key in searchPilotOverrides:
        flags[key] = searchPilotOverrides[key]!;
        break;
      case baseFlag.source === "searchPilot":
        flags[key] = baseFlag;
        break;

      case key in configOverrides:
        flags[key] = {
          source: "config",
          value: getParameterValue(configOverrides[key]),
        };
        break;

      default:
        flags[key] = baseFlag;
        break;
    }
  }

  return flags;
}

export function applyPageConfigOverrides(
  flags: FeatureFlags,
  configFlags?: ConfigFlags,
) {
  if (!configFlags) return flags;

  const result = {} as FeatureFlags;

  for (const [key, flag] of Object.entries(flags)) {
    if (flag.source === "default" && key in configFlags) {
      result[key] = {
        source: "config",
        value: configFlags[key]!,
      };
    } else {
      result[key] = flag;
    }
  }

  return result;
}

function getExperienceOverrides(
  experiences?: Experiences,
  parameterPrefix: string = "",
) {
  if (!experiences || experiences.length === 0) return {};

  return experiences.reduce(
    (acc: Record<string, MonetateFeatureFlag>, experience) => {
      Object.entries(experience.parameters || {})
        .filter(([parameterName]) => parameterName.startsWith(parameterPrefix))
        .forEach(([parameterName, parameterValue]) => {
          const flagName = parameterName.replace(parameterPrefix, "");
          const monetateFlag: MonetateFeatureFlag = {
            source: "monetate",
            value: getParameterValue(parameterValue),
            experienceName: experience.experienceName,
            experienceId: Number(experience.experienceId),
            impressionId: experience.impressionId ?? null,
            variantLabel: experience.variantLabel,
          };

          if (!(flagName in acc)) {
            acc[flagName] = monetateFlag;
          } else if (MULTI_EXPERIENCE_FLAG_NAMES.has(flagName)) {
            const existingFlag = acc[flagName]!;
            acc[flagName] = {
              ...existingFlag,
              additionalExperiences: [
                ...(existingFlag.additionalExperiences ?? []),
                monetateFlag,
              ],
            };
          }
        });
      return acc;
    },
    {},
  );
}

function getCookieOverrides(featureFlagOverridesCookie?: string) {
  if (!featureFlagOverridesCookie) return {};

  try {
    const parsed: unknown = JSON.parse(featureFlagOverridesCookie);
    const result = {} as Record<
      string,
      Extract<FeatureFlag, { source: "cookie" }>
    >;

    if (parsed && typeof parsed === "object") {
      for (const [key, value] of Object.entries(parsed)) {
        result[key] = {
          source: "cookie",
          value: getParameterValue(value),
        };
      }
    }

    return result;
  } catch {
    return {};
  }
}

function getSearchPilotOverrides(
  searchPilot: SearchPilotResult | undefined,
): FeatureFlags {
  if (!searchPilot) return {};
  const { variant, ...params } = searchPilot;
  return Object.entries(params).reduce(
    (acc, [flagName, value]) => {
      acc[flagName] = {
        source: "searchPilot",
        variant: variant,
        value: getParameterValue(value),
      };
      return acc;
    },
    {} as Record<string, Extract<FeatureFlag, { source: "searchPilot" }>>,
  );
}

const getParameterValue = (value: unknown): FeatureFlagValue => {
  if (["on", "true"].includes(String(value))) {
    return true;
  }
  if (["off", "false"].includes(String(value))) {
    return false;
  }
  return String(value);
};
